import React, { useState, useEffect } from "react";
import { Gift } from "lucide-react";
import { apiFetch } from "../../../../../context/AuthContext";
import AddAllowanceForm from "./AddAllowanceForm";
import AllowancesList from "./AllowancesList";
import { calculateGrossFromNet } from "../../../utils/marginalTax";


// Net vs Gross daily rate for a Days/Hours-based allowance line. Mirrors
// the backend's resolveEmployeeBaseGross (payrollService.js): prefer the
// employee's actual stored gross salary for this payroll when we have it,
// otherwise grossing up basicSalary the same way the backend does, so a
// Gross-mode day/hour entry is priced at the employee's real gross rate
// instead of silently reusing the net one.
const resolveDailyRate = (employee, isGross) => {
  const basicSalary = Number(employee?.basicSalary) || 0;
  const netDailyRate = employee?.dailyRate
    ? Number(employee.dailyRate)
    : basicSalary / 30;

  if (!isGross) return netDailyRate;

  // Always gross up THIS payroll's basicSalary rather than trusting the
  // employee's stored `grossSalary` field — that field can go stale (a raise
  // applied to basicSalary that grossSalary hasn't caught up with yet, or a
  // "new" gross set by some other flow), and pricing a Gross-mode day entry
  // off a mismatched gross salary silently mispreces it. basicSalary +
  // insuranceSalary are always this payroll's real, current figures.
  const insuranceSalary = Number(employee?.insuranceSalary) || 0;
  const grossedUp = calculateGrossFromNet(basicSalary, insuranceSalary);
  return grossedUp > 0 ? grossedUp / 30 : netDailyRate;
};


const AllowancesModal = ({
  isOpen,
  employee,
  onClose,
  API_BASE_URL,
  currentTheme,
  t,
  language,
  theme,
  isRTL,
  showAlert,
  screenSize,
}) => {
  const [allowances, setAllowances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAllowance, setNewAllowance] = useState({
    type: "",
    description: "",
    amount: "",
    days: "",
    hours: "",
    percentage: "",
    baseAmount: "",
    isTaxable: true,
    isRecurring: false,
  });
  const [editingAllowanceId, setEditingAllowanceId] = useState(null);
  const gridCols = screenSize === "xs" ? 1 : screenSize === "sm" ? 2 : 4;

  useEffect(() => {
    if (isOpen && employee?.id) {
      fetchAllowances();
    }
  }, [isOpen, employee?.id]);

  const resetForm = () => {
    setNewAllowance({
      type: "",
      description: "",
      amount: "",
      days: "",
      hours: "",
      percentage: "",
      baseAmount: "",
      isTaxable: true,
      isRecurring: false,
    });
    setEditingAllowanceId(null);
  };

  const fetchAllowances = async () => {
    setLoading(true);
    try {
      const url = `${API_BASE_URL}/payrollX/${employee.id}/allowances`;
      const response = await apiFetch(url, { method: "GET" });
      if (response.ok) {
        const result = await response.json();
        setAllowances(result?.data?.allowances || []);
      }
    } catch (err) {
      console.error("Error fetching allowances:", err);
    } finally {
      setLoading(false);
    }
  };

  // Live preview: recompute `amount` the moment Days/Hours/Percentage
  // drives a value, the same way the Deduction modal's late-arrival hours
  // auto-fill amount as you type — so entering a day/hour count is enough
  // on its own; you never have to also type the Amount by hand. Typing
  // Amount directly still works and is left alone whenever none of these
  // driver fields have a value.
  useEffect(() => {
    const days = newAllowance.days ? parseFloat(newAllowance.days) : null;
    const hours = newAllowance.hours ? parseFloat(newAllowance.hours) : null;
    const percentage = newAllowance.percentage
      ? parseFloat(newAllowance.percentage)
      : null;
    const baseAmount = newAllowance.baseAmount
      ? parseFloat(newAllowance.baseAmount)
      : null;
    const isOvertimeType = newAllowance.type === "overtime";

    // Gross-mode entries price off the employee's real gross daily rate,
    // not the net one — matches the (Net)/(Gross) column pair the bulk
    // import template and backend already use for the same distinction.
    const dailyRate = resolveDailyRate(employee, newAllowance.isTaxable);

    let computed = null;
    if (percentage && baseAmount) {
      computed = (baseAmount * percentage) / 100;
    } else if (isOvertimeType && hours) {
      // hourlyRate = dailyRate / 8, same 1.5x fallback multiplier the
      // bulk-import template uses when the company's configured Overtime
      // Rate isn't available on the client.
      computed = (dailyRate / 8) * 1.5 * hours;
    } else if (!isOvertimeType && days) {
      computed = dailyRate * days;
    }

    if (computed !== null && computed > 0) {
      const rounded = computed.toFixed(2);
      setNewAllowance((prev) =>
        prev.amount === rounded ? prev : { ...prev, amount: rounded },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    newAllowance.days,
    newAllowance.hours,
    newAllowance.percentage,
    newAllowance.baseAmount,
    newAllowance.type,
    newAllowance.isTaxable,
  ]);


  const handleAddAllowance = async () => {
    if (!newAllowance.type) {
      showAlert(t("important"), t("fillTypeAmount"), "info");
      return;
    }

    const days = newAllowance.days ? parseFloat(newAllowance.days) : null;
    const hours = newAllowance.hours ? parseFloat(newAllowance.hours) : null;
    const percentage = newAllowance.percentage
      ? parseFloat(newAllowance.percentage)
      : null;
    const baseAmount = newAllowance.baseAmount
      ? parseFloat(newAllowance.baseAmount)
      : null;
    const isOvertime = newAllowance.type === "overtime";
    let amount = newAllowance.amount ? parseFloat(newAllowance.amount) : 0;

    // 1) Percentage-based: amount = baseAmount * percentage / 100
    if ((!amount || amount === 0) && percentage && baseAmount) {
      amount = (baseAmount * percentage) / 100;
    }

    // 2) Days/Hours-based fallback (the live preview effect above already
    // fills this in as you type — this just covers amount still being 0 at
    // submit time, e.g. programmatic updates that skipped the effect).
    // Overtime uses Hours (hourlyRate = dailyRate / 8, 1.5x fallback
    // multiplier); every other type uses Days (full dailyRate).
    if ((!amount || amount === 0) && ((isOvertime && hours) || (!isOvertime && days))) {
      const dailyRate = resolveDailyRate(employee, newAllowance.isTaxable);
      amount = isOvertime ? (dailyRate / 8) * 1.5 * hours : days * dailyRate;
    }

    if (!amount || isNaN(amount) || amount === 0) {
      showAlert(t("important"), t("fillTypeAmount"), "info");
      return;
    }

    setLoading(true);
    try {
      const isEditing = !!editingAllowanceId;
      const url = isEditing
        ? `${API_BASE_URL}/payrollX/allowances/${editingAllowanceId}`
        : `${API_BASE_URL}/payrollX/${employee.id}/allowances`;

      const response = await apiFetch(url, {
        method: isEditing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: newAllowance.type,
          description: newAllowance.description,
          amount,
          days,
          hours,
          percentage,
          baseAmount,
          isTaxable: newAllowance.isTaxable,
          isRecurring: newAllowance.isRecurring,
        }),
      });

      if (response.ok) {
        await fetchAllowances();
        resetForm();
        setShowAddForm(false);
        showAlert(
          t("success"),
          isEditing
            ? t("allowanceUpdatedSuccess") || "Allowance updated successfully"
            : t("allowanceAddedSuccess") || "Allowance added successfully",
          "success"
        );
      }
    } catch (err) {
      console.error("Error saving allowance:", err);
      showAlert(
        t("error"),
        t("errorAddingAllowance") || "Error adding allowance",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    return allowances.reduce((sum, a) => sum + Number(a.amount || 0), 0);
  };

  const handleDeleteAllowance = async (allowanceId) => {
    if (
      !window.confirm(
        t("confirmDeleteAllowance") ||
          "Are you sure you want to delete this allowance?"
      )
    ) {
      return;
    }
    setLoading(true);
    try {
      const url = `${API_BASE_URL}/payrollX/allowances/${allowanceId}`;
      const response = await apiFetch(url, { method: "DELETE" });
      if (response.ok) {
        showAlert(
          t("success"),
          t("allowanceDeletedSuccess") || "Allowance deleted successfully",
          "success"
        );
        fetchAllowances();
        if (editingAllowanceId === allowanceId) {
          resetForm();
          setShowAddForm(false);
        }
      } else {
        showAlert(
          t("error"),
          t("errorDeletingAllowance") || "Failed to delete allowance",
          "error"
        );
      }
    } catch (err) {
      console.error("Error deleting allowance:", err);
      showAlert(
        t("error"),
        t("errorDeletingAllowance") || "Failed to delete allowance",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (allowance) => {
    setEditingAllowanceId(allowance._id || allowance.id);
    setNewAllowance({
      type: allowance.type,
      description: allowance.description || "",
      amount: allowance.amount,
      days: allowance.days || "",
      hours: allowance.hours || "",
      percentage: allowance.percentage || "",
      baseAmount: allowance.baseAmount || "",
      isTaxable: allowance.isTaxable ?? true,
      isRecurring: allowance.isRecurring ?? false,
    });
    setShowAddForm(true);
  };

  const handleToggleForm = () => {
    if (showAddForm) {
      setShowAddForm(false);
      resetForm();
    } else {
      setShowAddForm(true);
    }
  };

  if (!isOpen) return null;

  const formStyles = {
    modal: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.8)",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      zIndex: 3000,
      padding: "20px",
      overflowY: "auto",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      WebkitOverflowScrolling: "touch",
    },
    modalContent: {
      background: currentTheme.colors.cardBackground,
      backdropFilter: "blur(25px)",
      border: `2px solid ${currentTheme.colors.border}`,
      borderRadius: "24px",
      padding: "40px",
      width: "100%",
      maxWidth: "900px",
      margin: "20px auto",
      maxHeight: "90vh",
      overflowY: "auto",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      WebkitOverflowScrolling: "touch",
      boxShadow: currentTheme.shadows.card,
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "32px",
      paddingBottom: "20px",
      borderBottom: `2px solid ${currentTheme.colors.border}`,
    },
    title: {
      color: currentTheme.colors.textPrimary,
      fontSize: "28px",
      fontWeight: "800",
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    section: {
      background: currentTheme.colors.inputBackground,
      borderRadius: "20px",
      padding: "24px",
      border: `2px solid ${currentTheme.colors.border}`,
      marginBottom: "24px",
    },
    allowanceCard: {
      position: "relative",
      overflow: "hidden",
      background: currentTheme.colors.cardBackground,
      borderRadius: "14px",
      padding: "18px 20px 18px 24px",
      border: `1px solid ${currentTheme.colors.borderLight}`,
      marginBottom: "14px",
      transition: "all 0.25s ease",
      boxShadow: theme === "dark" ? "0 2px 8px rgba(0,0,0,0.18)" : "0 2px 8px rgba(0,0,0,0.04)",
    },
    formGrid: {
      display: "grid",
      gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
      gap: "16px",
      marginBottom: "20px",
    },
    formGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    label: {
      color: currentTheme.colors.textSecondary,
      fontSize: "14px",
      fontWeight: "600",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    input: {
      background: currentTheme.colors.inputBackground,
      border: `2px solid ${currentTheme.colors.border}`,
      borderRadius: "12px",
      padding: "12px 16px",
      color: currentTheme.colors.textPrimary,
      fontSize: "14px",
      fontWeight: "500",
      transition: "all 0.3s ease",
      outline: "none",
    },
  };

  return (
    <div
      style={formStyles.modal}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div style={formStyles.modalContent}>
        <div style={formStyles.header}>
          <h1 style={formStyles.title}>
            <Gift size={32} color={currentTheme.colors.success} />
            {t("employeeAllowances") || "Allowances"}{" "}
            {employee?.employeeName?.split(" ").slice(0, 2).join(" ")}
          </h1>
        </div>

        {/* Add Allowance Form */}
        <AddAllowanceForm
          t={t}
          theme={theme}
          currentTheme={currentTheme}
          formStyles={formStyles}
          showAddForm={showAddForm}
          setShowAddForm={handleToggleForm}
          newAllowance={newAllowance}
          setNewAllowance={setNewAllowance}
          handleAddAllowance={handleAddAllowance}
          loading={loading}
          language={language}
          editingAllowanceId={editingAllowanceId}
        />

        {/* Allowances List */}
        <AllowancesList
          t={t}
          currentTheme={currentTheme}
          formStyles={formStyles}
          isRTL={isRTL}
          loading={loading}
          allowances={allowances}
          onDeleteAllowance={handleDeleteAllowance}
          onEditAllowance={handleEditClick}
        />
      </div>
    </div>
  );
};

export default AllowancesModal;